import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Users,
  ClipboardList,
  CheckSquare,
  Edit,
  Trash2,
  MessageSquare,
  FileText,
  Mail,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { TabGroup } from '@/components/ui/tab-group'
import { NodeUsers, NodeCard, NodeList, NodePropertyString } from '@/components/nodes'
import { workspaceService } from '@/services'
import { Workspace, MessageStatus, Status } from '@/types'
import { useAuth } from '@/hooks/useAuth'
import {
  workspaceDetailTabs,
  workspaceProjectsTabs,
  workspaceTasksTabs,
  workspaceMessagesTabs,
} from '@/lib/config/tabs'
import { PageContainer, PageHeader, LoadingState } from '@/components/layout'

export default function WorkspaceDetailPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showAllCompletedProjects, setShowAllCompletedProjects] = useState(false)
  const [showAllMessages, setShowAllMessages] = useState(false)
  const [showAllHomeMyTasks, setShowAllHomeMyTasks] = useState(false)
  const [showAllHomeMyProjects, setShowAllHomeMyProjects] = useState(false)
  const [showAllHomeMessages, setShowAllHomeMessages] = useState(false)
  const [activeTab, setActiveTab] = useState('home')
  const [projectsTab, setProjectsTab] = useState('active')
  const [tasksTab, setTasksTab] = useState('my')
  const [messagesTab, setMessagesTab] = useState('all')

  const loadWorkspace = useCallback(async () => {
    if (!workspaceId) return

    try {
      const data = await workspaceService.getByIdWithTasks(workspaceId)
      if (data) {
        setWorkspace(data)
      } else {
        toast.error('Workspace not found')
        navigate('/workspaces')
      }
    } catch {
      toast.error('Failed to load workspace')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId, navigate])

  useEffect(() => {
    loadWorkspace()
  }, [loadWorkspace])

  const handleDelete = async () => {
    if (!workspace || !user) return

    if (workspace.creator.id !== user.id) {
      toast.error('Only the workspace creator can delete this workspace')
      return
    }

    if (!confirm('Are you sure you want to delete this workspace? This action cannot be undone.')) {
      return
    }

    setIsDeleting(true)
    try {
      await workspaceService.delete(workspace.id)
      toast.success('Workspace deleted successfully')
      navigate('/workspaces')
    } catch {
      toast.error('Failed to delete workspace')
    } finally {
      setIsDeleting(false)
    }
  }

  const isCreator = user && workspace && workspace.creator.id === user.id
  const isMember = user && workspace && workspace.users.some((u) => u.id === user.id)

  // Get project IDs that belong to the current workspace
  const workspaceProjectIds = new Set(workspace?.projects.map((project) => project.id) || [])

  // Filter messages by category
  const workspaceMessages =
    workspace?.messages.filter(
      (msg) =>
        msg.projects.length === 0 &&
        msg.tasks.length === 0 &&
        msg.documents.length === 0 &&
        msg.status !== MessageStatus.Draft
    ) || []

  // Filter documents for current user
  const myDocuments = (
    workspace?.documents.filter((doc) => {
      // Document created by current user
      const isCreator = doc.creator.id === user?.id

      // Document associated with projects that belong to the current workspace
      const hasWorkspaceProject = doc.projects.some((project) =>
        workspaceProjectIds.has(project.id)
      )

      // Document is workspace-only (not associated with any projects, tasks, or other nodes)
      const isWorkspaceOnly = doc.projects.length === 0 && doc.tasks.length === 0

      return isCreator || hasWorkspaceProject || isWorkspaceOnly
    }) || []
  ).sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  const workMessages = (
    workspace?.messages.filter((msg) => {
      // Check if message is associated with any projects that belong to the current workspace
      const hasWorkspaceProject = msg.projects.some((project) =>
        workspaceProjectIds.has(project.id)
      )
      return hasWorkspaceProject && msg.status !== MessageStatus.Draft
    }) || []
  ).sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
  const draftMessages = (
    workspace?.messages.filter((msg) => {
      // Include draft messages that are either:
      // 1. Associated with projects that belong to the current workspace, OR
      // 2. Only associated with the current workspace (workspace-only messages)
      if (msg.status !== MessageStatus.Draft) return false

      const hasWorkspaceProject = msg.projects.some((project) =>
        workspaceProjectIds.has(project.id)
      )
      const isWorkspaceOnly =
        msg.projects.length === 0 && msg.tasks.length === 0 && msg.documents.length === 0

      return hasWorkspaceProject || isWorkspaceOnly
    }) || []
  ).sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  // Filter and sort all messages (excluding drafts) by created date (newest first)
  const allMessages = (
    workspace?.messages.filter((msg) => msg.status !== MessageStatus.Draft) || []
  ).sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  // Filter and sort workspace-only messages by created date (newest first)
  const sortedWorkspaceMessages = (
    workspace?.messages.filter(
      (msg) =>
        msg.projects.length === 0 &&
        msg.tasks.length === 0 &&
        msg.documents.length === 0 &&
        msg.status !== MessageStatus.Draft
    ) || []
  ).sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  // Filter projects by status
  const activeProjects =
    workspace?.projects.filter(
      (project) => project.status === Status.ToDo || project.status === Status.InProgress
    ) || []
  const completedProjects =
    workspace?.projects.filter((project) => project.status === Status.Done) || []
  const draftProjects =
    workspace?.projects.filter((project) => project.status === Status.Draft) || []

  // Filter tasks by category
  const myTasks = (
    workspace?.tasks.filter(
      (task) =>
        task.assignee?.id === user?.id &&
        task.status !== Status.Draft &&
        task.status !== Status.Done
    ) || []
  ).sort((a, b) => {
    // Tasks without due dates go to the end
    if (!a.dueDate && !b.dueDate) return 0
    if (!a.dueDate) return 1
    if (!b.dueDate) return -1

    // Sort by due date (earliest first, which puts overdue tasks at top)
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  })
  const collaborativeTasks = (
    workspace?.tasks.filter(
      (task) =>
        task.users.some((u) => u.id === user?.id) &&
        task.assignee?.id !== user?.id &&
        task.status !== Status.Draft &&
        task.status !== Status.Done
    ) || []
  ).sort((a, b) => {
    // Tasks without due dates go to the end
    if (!a.dueDate && !b.dueDate) return 0
    if (!a.dueDate) return 1
    if (!b.dueDate) return -1

    // Sort by due date (earliest first, which puts overdue tasks at top)
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  })
  const allTasks = (
    workspace?.tasks.filter(
      (task) =>
        task && task.id && task.name && task.status !== Status.Draft && task.status !== Status.Done
    ) || []
  ).sort((a, b) => {
    // Tasks without due dates go to the end
    if (!a.dueDate && !b.dueDate) return 0
    if (!a.dueDate) return 1
    if (!b.dueDate) return -1

    // Sort by due date (earliest first, which puts overdue tasks at top)
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  })
  const draftTasks = (
    workspace?.tasks.filter(
      (task) => task.creator.id === user?.id && task.status === Status.Draft
    ) || []
  ).sort((a, b) => {
    // Sort by created date (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  // Filter data for Home tab
  const myProjects =
    workspace?.projects.filter(
      (project) =>
        project.users.some((u) => u.id === user?.id) &&
        (project.status === Status.ToDo || project.status === Status.InProgress)
    ) || []
  const unreadMessages = (
    workspace?.messages.filter((msg) => {
      // Only include messages where the current user is involved
      if (!msg.users.some((u) => u.id === user?.id)) return false

      // Only include published messages
      if (msg.status !== MessageStatus.Published) return false

      // Include messages that are either:
      // 1. Associated with projects that belong to the current workspace, OR
      // 2. Only associated with the current workspace (workspace-only messages)
      const hasWorkspaceProject = msg.projects.some((project) =>
        workspaceProjectIds.has(project.id)
      )
      const isWorkspaceOnly =
        msg.projects.length === 0 && msg.tasks.length === 0 && msg.documents.length === 0

      return hasWorkspaceProject || isWorkspaceOnly
    }) || []
  ).sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  if (isLoading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState text="Loading workspace..." />
      </PageContainer>
    )
  }

  if (!workspace) {
    return null
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title={workspace.name}
        description={workspace.description}
        metadata={
          <>
            <NodePropertyString description={workspace.email} icon={Mail} textSize="sm" />
            <NodeUsers
              users={workspace.users}
              maxVisible={10}
              avatarSize="md"
              viewAllUrl={`/workspaces/${workspace.id}/members`}
              viewAllLabel="View all"
              icon={Users}
            />
          </>
        }
        customActions={
          isMember && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/workspaces/${workspace.id}/edit`)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              {isCreator && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="space-y-6 mt-6">
        {/* Tabbed Content Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabGroup
            tabs={workspaceDetailTabs}
            badges={{
              projects: workspace.projects.length,
              tasks: workspace.tasks.length,
              messages: workspace.messages.length,
              documents: workspace.documents.length,
            }}
            value={activeTab}
            onValueChange={setActiveTab}
          />

          <TabsContent value="home">
            <div className="grid gap-6 md:grid-cols-2">
              {/* My Tasks Card */}
              <NodeCard
                title="My Tasks"
                description={`${myTasks.length} ${myTasks.length === 1 ? 'incomplete task' : 'incomplete tasks'} assigned to you in ${workspace.name}`}
                icon={<CheckSquare className="h-5 w-5" />}
                items={myTasks}
                context="task"
                config={{
                  showStatus: true,
                  showPriority: true,
                  showDescription: false,
                  showWorkspace: false,
                  showAssignee: false,
                  showDueDate: true,
                  showSubtasks: false,
                  hoverMode: true,
                  maxVisible: showAllHomeMyTasks ? undefined : 5,
                  emptyState: 'No tasks assigned to you',
                  onShowMore: () => setShowAllHomeMyTasks(true),
                }}
              />

              {/* My Projects Card */}
              <NodeCard
                title="My Projects"
                description={`${myProjects.length} ${myProjects.length === 1 ? 'active project' : 'active projects'} you're collaborating on in ${workspace.name}`}
                icon={<ClipboardList className="h-5 w-5" />}
                items={myProjects}
                context="project"
                config={{
                  showStatus: true,
                  showWorkspace: false,
                  showUserCount: false,
                  showDescription: true,
                  showCreator: false,
                  hoverMode: true,
                  avatarSize: 'sm',
                  maxVisible: showAllHomeMyProjects ? undefined : 5,
                  emptyState: 'No projects yet',
                  onShowMore: () => setShowAllHomeMyProjects(true),
                }}
              />

              {/* Unread Messages Card */}
              <NodeCard
                title="Unread Messages"
                description={`${unreadMessages.length} ${unreadMessages.length === 1 ? 'unread message' : 'unread messages'} you haven't read in ${workspace.name}`}
                icon={<MessageSquare className="h-5 w-5" />}
                items={unreadMessages}
                context="message"
                config={{
                  showCreator: true,
                  showDate: true,
                  showReplyCount: true,
                  showWorkspace: false,
                  maxVisible: showAllHomeMessages ? undefined : 3,
                  emptyState: 'No unread messages',
                  onShowMore: () => setShowAllHomeMessages(true),
                }}
              />

              {/* My Documents Card */}
              <NodeCard
                title="My Documents"
                description={`${myDocuments.length} ${myDocuments.length === 1 ? 'document' : 'documents'} you've created or are associated with in ${workspace.name}`}
                icon={<FileText className="h-5 w-5" />}
                items={myDocuments}
                context="document"
                config={{
                  showType: true,
                  showTypeIcon: true,
                  showDescription: true,
                  showWorkspace: false,
                  showCreator: false,
                  showDate: false,
                  showSharedCount: false,
                  maxVisible: 5,
                  emptyState: 'No documents yet',
                }}
              />
            </div>
          </TabsContent>

          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      Projects
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {workspace.projects.length} projects in this workspace
                    </CardDescription>
                  </div>
                  {isMember && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/projects')}>
                      <ClipboardList className="h-4 w-4 mr-2" />
                      View All Projects
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={projectsTab} onValueChange={setProjectsTab} className="w-full">
                  <TabGroup
                    tabs={workspaceProjectsTabs}
                    badges={{
                      active: activeProjects.length,
                      completed: completedProjects.length,
                      draft: draftProjects.length,
                    }}
                    value={projectsTab}
                    onValueChange={setProjectsTab}
                  />

                  <TabsContent value="active">
                    <NodeCard
                      title="Active Projects"
                      description="Projects that need to be completed or are in progress"
                      items={activeProjects}
                      context="project"
                      config={{
                        showStatus: true,
                        showWorkspace: false,
                        showUserCount: false,
                        showDescription: true,
                        showCreator: false,
                        avatarSize: 'md',
                        emptyState: 'No active projects yet',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="completed">
                    <NodeCard
                      title="Completed Projects"
                      description="Projects that have been completed"
                      items={completedProjects}
                      context="project"
                      config={{
                        showStatus: false,
                        showWorkspace: false,
                        showUserCount: false,
                        showDescription: true,
                        showCreator: false,
                        avatarSize: 'md',
                        showHorizon: false,
                        showPriority: false,
                        maxVisible: showAllCompletedProjects ? undefined : 10,
                        emptyState: 'No completed projects yet',
                        onShowMore: () => setShowAllCompletedProjects(true),
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="draft">
                    <NodeCard
                      title="Draft Projects"
                      description="Projects that are a draft"
                      items={draftProjects}
                      context="project"
                      config={{
                        showStatus: false,
                        showPriority: false,
                        showHorizon: false,
                        showWorkspace: false,
                        showUserCount: false,
                        showDescription: true,
                        showCreator: true,
                        avatarSize: 'md',
                        emptyState: 'No draft projects yet',
                      }}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5" />
                      Tasks
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {workspace.tasks.length} tasks in this workspace
                    </CardDescription>
                  </div>
                  {isMember && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/tasks')}>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      View All Tasks
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={tasksTab} onValueChange={setTasksTab} className="w-full">
                  <TabGroup
                    tabs={workspaceTasksTabs}
                    badges={{
                      my: myTasks.length,
                      collaborative: collaborativeTasks.length,
                      all: allTasks.length,
                      draft: draftTasks.length,
                    }}
                    value={tasksTab}
                    onValueChange={setTasksTab}
                  />

                  <TabsContent value="my">
                    <NodeCard
                      title="My Tasks"
                      description="Tasks assigned to me"
                      items={myTasks}
                      context="task"
                      config={{
                        showStatus: true,
                        showPriority: true,
                        showDescription: true,
                        showWorkspace: false,
                        showAssignee: false,
                        showDueDate: true,
                        showSubtasks: false,
                        emptyState: 'No tasks assigned to you yet',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="collaborative">
                    <NodeCard
                      title="Collaborative Tasks"
                      description="Tasks I'm collaborating on"
                      items={collaborativeTasks}
                      context="task"
                      config={{
                        showStatus: true,
                        showPriority: true,
                        showDescription: true,
                        showWorkspace: false,
                        showAssignee: true,
                        showDueDate: true,
                        showSubtasks: false,
                        emptyState: 'No collaborative tasks yet',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="all">
                    <NodeCard
                      title="All Tasks"
                      description="All tasks in this workspace"
                      items={allTasks}
                      context="task"
                      config={{
                        showStatus: true,
                        showPriority: true,
                        showDescription: true,
                        showWorkspace: false,
                        showAssignee: true,
                        showDueDate: true,
                        showSubtasks: false,
                        emptyState: 'No tasks in this workspace yet',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="draft">
                    <NodeCard
                      title="Draft Tasks"
                      description="Tasks that are a draft"
                      items={draftTasks}
                      context="task"
                      config={{
                        showStatus: true,
                        showPriority: true,
                        showDescription: true,
                        showWorkspace: false,
                        showAssignee: true,
                        showDueDate: true,
                        showSubtasks: false,
                        emptyState: 'No draft tasks yet',
                      }}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Messages
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {workspace.messages.length} messages in this workspace
                    </CardDescription>
                  </div>
                  {isMember && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/messages')}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      View All Messages
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={messagesTab} onValueChange={setMessagesTab} className="w-full">
                  <TabGroup
                    tabs={workspaceMessagesTabs}
                    badges={{
                      all: workspace.messages.length,
                      workspace: workspaceMessages.length,
                      work: workMessages.length,
                      draft: draftMessages.length,
                    }}
                    value={messagesTab}
                    onValueChange={setMessagesTab}
                  />

                  <TabsContent value="all">
                    <NodeList
                      items={allMessages}
                      context="message"
                      config={{
                        showCreator: true,
                        showDate: true,
                        showReplyCount: true,
                        maxVisible: showAllMessages ? undefined : 10,
                        onShowMore: () => setShowAllMessages(true),
                        emptyState: 'No messages in this workspace yet',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="workspace">
                    <NodeList
                      items={sortedWorkspaceMessages}
                      context="message"
                      config={{
                        showCreator: true,
                        showDate: true,
                        showReplyCount: true,
                        maxVisible: showAllMessages ? undefined : 10,
                        onShowMore: () => setShowAllMessages(true),
                        emptyState: 'No workspace-only messages yet',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="work">
                    <NodeList
                      items={workMessages}
                      context="message"
                      config={{
                        showCreator: true,
                        showDate: true,
                        showReplyCount: true,
                        maxVisible: showAllMessages ? undefined : 10,
                        onShowMore: () => setShowAllMessages(true),
                        emptyState: 'No work-related messages (projects, tasks, or documents) yet',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="draft">
                    <NodeList
                      items={draftMessages}
                      context="message"
                      config={{
                        showCreator: true,
                        showDate: true,
                        showReplyCount: true,
                        maxVisible: showAllMessages ? undefined : 10,
                        onShowMore: () => setShowAllMessages(true),
                        emptyState: 'No draft messages yet',
                      }}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <NodeCard
              title="Documents"
              description={`${
                workspace.documents.filter((doc) => {
                  // Document associated with projects that belong to the current workspace
                  const hasWorkspaceProject = doc.projects.some((project) =>
                    workspaceProjectIds.has(project.id)
                  )

                  // Document is workspace-only (not associated with any projects, tasks, or other nodes)
                  const isWorkspaceOnly = doc.projects.length === 0 && doc.tasks.length === 0

                  return hasWorkspaceProject || isWorkspaceOnly
                }).length
              } documents in this workspace`}
              icon={<FileText className="h-5 w-5" />}
              items={workspace.documents
                .filter((doc) => {
                  // Document associated with projects that belong to the current workspace
                  const hasWorkspaceProject = doc.projects.some((project) =>
                    workspaceProjectIds.has(project.id)
                  )

                  // Document is workspace-only (not associated with any projects, tasks, or other nodes)
                  const isWorkspaceOnly = doc.projects.length === 0 && doc.tasks.length === 0

                  return hasWorkspaceProject || isWorkspaceOnly
                })
                .sort((a, b) => {
                  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                })}
              context="document"
              config={{
                showType: true,
                showTypeIcon: true,
                showDescription: true,
                showWorkspace: false,
                showCreator: true,
                showCreatorName: true,
                showDate: true,
                showSharedCount: false,
                emptyState: 'No documents in this workspace yet',
              }}
              action={
                isMember ? (
                  <Button variant="outline" size="sm" onClick={() => navigate('/documents')}>
                    <FileText className="h-4 w-4 mr-2" />
                    View All Documents
                  </Button>
                ) : undefined
              }
            />
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  )
}
